import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import HeroSection from '../components/home/HeroSection';
import FourPillarsStrip from '../components/home/FourPillarsStrip';
import JourneyResume from '../components/journey/JourneyResume';
import TrustStrip from '../components/home/TrustStrip';
import LiveDataStrip from '../components/home/LiveDataStrip';
import RegionGrid from '../components/home/RegionGrid';
import LatestNewsStrip from '../components/home/LatestNewsStrip';
import HomepageLivePackagesStrip from '../components/home/HomepageLivePackagesStrip';

import HomeFinishedBuildsReel from '../components/home/HomeFinishedBuildsReel';
import ProcessSection from '../components/home/ProcessSection';
import BuilderPartnersSection from '../components/home/BuilderPartnersSection';
import AppPromoSection from '../components/home/AppPromoSection';
import FeaturedOnStrip from '../components/home/FeaturedOnStrip';
import ShortlistStartersSection from '../components/home/ShortlistStartersSection';
import CollectionsStrip from '../components/home/CollectionsStrip';
import { HOME_STYLES, HOME_STYLE_GROUPS } from '../data/homeStyles';
import FAQSection from '../components/home/FAQSection';
import CTASection from '../components/home/CTASection';

import { SEOSchemas } from '../components/seo/SEOSchemas';
import { SiteNavigationSchema } from '../components/seo/SiteSchema';
import AIOverviewSchema from '../components/seo/AIOverviewSchema';
import Reveal from '../components/motion/ScrollReveal';
import SeasonStrip from '../components/home/SeasonStrip';
import { useUserState, STATE_NAMES, isLiveState } from '../lib/userState';
import { resolveJourney } from '../data/journeyResolver';
import useSessionContact from '../hooks/useSessionContact';

// Build-type/journey projects that signal genuine new-build / land intent -
// the only audience for whom homepage H&L packages are relevant ("tailored").
const HL_INTENT_PROJECTS = new Set([
  'building-a-new-home',
  'house-and-land-adelaide',
  'choosing-land',
  'first-home-buyer-adelaide',
  'property-development',
]);

// Internal-link block link sets. SA continues to link into the deep
// Adelaide programmatic library; VIC surfaces only pages we actually
// ship for VIC today (hub, grants, councils, waitlist) plus the
// state-agnostic help centre content so the section still feels
// substantial without inventing pages that do not exist yet.
const SA_INTERNAL_LINKS = [
  { to: '/building-a-new-home', label: 'Building a New Home' },
  { to: '/renovations', label: 'Home Renovations Adelaide' },
  { to: '/swimming-pools', label: 'Pool Builders Adelaide' },
  { to: '/verandahs-pergolas', label: 'Verandahs & Pergolas Adelaide' },
  { to: '/decking', label: 'Decking Adelaide' },
  { to: '/landscaping', label: 'Landscaping Adelaide' },
  { to: '/choosing-land', label: 'Choosing the Right Land' },
  { to: '/choosing-land/soil-types', label: 'Soil Types & Site Costs' },
  { to: '/knockdown-rebuild', label: 'Knockdown Rebuild Guide' },
  { to: '/knockdown-rebuild/costs', label: 'KDR Cost Breakdown' },
  { to: '/renovations/costs', label: 'Renovation Costs 2026' },
  { to: '/building-a-new-home/budget', label: 'Building Budget Guide' },
  { to: '/building-a-new-home/build-timeline', label: 'Build Timeline' },
  { to: '/terminology', label: 'Building Glossary (128 Terms)' },
  { to: '/terminology/site-costs', label: 'Site Costs Explained' },
  { to: '/terminology/provisional-sums', label: 'Provisional Sums' },
  { to: '/terminology/eaves', label: 'Eaves - Do You Need Them?' },
  { to: '/home-builders-adelaide-hills', label: 'Adelaide Hills Builders' },
  { to: '/home-builders-adelaide', label: 'Best Builders Adelaide' },
  { to: '/home-designs', label: 'Home Designs & Floor Plans' },
  { to: '/house-and-land-adelaide', label: 'House & Land Packages Adelaide' },
  { to: '/building-inspectors', label: 'Building Inspectors' },
  { to: '/help/cheapest-land-south-australia', label: 'Cheapest Land in SA' },
];

const VIC_INTERNAL_LINKS = [
  { to: '/vic', label: 'Victoria Hub' },
  { to: '/vic/grants', label: 'Victorian Grants & Concessions' },
  { to: '/vic/building-in/melbourne-council-vic', label: 'Building in Melbourne' },
  { to: '/vic/building-in/yarra-council-vic', label: 'Building in Yarra' },
  { to: '/vic/building-in/port-phillip-council-vic', label: 'Building in Port Phillip' },
  { to: '/vic/building-in/stonnington-council-vic', label: 'Building in Stonnington' },
  { to: '/vic/building-in/boroondara-council-vic', label: 'Building in Boroondara' },
  { to: '/vic/building-in/whittlesea-council-vic', label: 'Building in Whittlesea' },
  { to: '/vic/building-in/glen-eira-council-vic', label: 'Building in Glen Eira' },
  { to: '/vic/building-in/whitehorse-council-vic', label: 'Building in Whitehorse' },
  { to: '/vic/building-in/bayside-council-vic', label: 'Building in Bayside' },
  { to: '/building-a-new-home', label: 'Building a New Home' },
  { to: '/renovations', label: 'Home Renovations' },
  { to: '/choosing-land', label: 'Choosing the Right Land' },
  { to: '/knockdown-rebuild', label: 'Knockdown Rebuild Guide' },
  { to: '/terminology', label: 'Building Glossary (128 Terms)' },
  { to: '/terminology/site-costs', label: 'Site Costs Explained' },
  { to: '/home-builders-melbourne', label: 'Melbourne Home Builders' },
  { to: '/house-and-land-melbourne', label: 'House & Land Packages Melbourne' },
];

// Cards shown to VIC visitors in place of the SA-only "Live now in
// Adelaide" H&L strip. Points at the four VIC surfaces that actually
// exist today so the section is honest, not aspirational.
const VIC_EXPLORE_CARDS = [
  {
    eyebrow: 'Grants',
    title: 'Victorian grants and concessions',
    body: 'FHOG plus the stamp duty concession in plain English, with a direct link to the State Revenue Office for current amounts.',
    to: '/vic/grants',
  },
  {
    eyebrow: 'Councils',
    title: 'Melbourne council building guides',
    body: '9 Melbourne local government areas, each with the state permit process, local overlays and contact details.',
    to: '/vic',
  },
  {
    eyebrow: 'Suburbs',
    title: 'Victorian suburb list',
    body: '29 suburbs across Victoria with BuildPilot house-and-land coverage. Open any suburb for local building guidance and packages as they appear.',
    to: '/vic',
  },
  {
    eyebrow: 'Builders',
    title: 'Find your Melbourne builder',
    body: "Tell us your build type, budget and block and we'll match you with vetted Victorian builders suited to your project - free.",
    to: '/matcher?category=professional:builder&state=VIC&source=home-vic-strip&heading=Find%20your%20Melbourne%20builder',
  },
];

// "Coming soon to {state}" waitlist callout for non-live states
// (NSW / QLD / WA / TAS / NT / ACT). Rendered in place of the SA-only
// live packages strip so the home page never leaves someone from
// (say) Sydney staring at Adelaide-suburb tiles.
const NonLiveStateCallout = ({ stateName, stateCode }) => (
  <section className="py-9 sm:py-16 bg-white border-t border-gray-100" data-testid="non-live-state-callout" data-non-live-state={stateCode}>
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-[#1d8efe] animate-pulse" />
        <p className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe]">
          Now expanding &middot; {stateName}
        </p>
      </div>
      <h2 className="text-[26px] sm:text-[34px] font-bold text-[#1d1d1f] tracking-tight leading-[1.15] mb-3 sm:mb-4">
        You're already in. <span className="gradient-text-blue">{stateName} is next</span> for live matching.
      </h2>
      <p className="text-[15px] sm:text-[16px] text-[#86868b] max-w-[640px] mx-auto leading-[1.55] mb-6 sm:mb-8">
        BuildPilot's tools, guides, costs and designs are national - they work in {stateName} today. Live builder matching and house &amp; land packages are already running in South Australia and Victoria, and {stateName} is next on the rollout. Join the waitlist and we'll tell you the day local matching opens near you.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to={`/waitlist?state=${stateCode}`}
          className="inline-flex items-center justify-center gap-1.5 bg-[#1d1d1f] text-white hover:bg-black px-6 py-3 rounded-full text-[14px] font-semibold transition-all"
          data-testid="non-live-state-waitlist-cta"
        >
          Get {stateName} live-matching updates
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/one-on-one-support"
          className="inline-flex items-center justify-center gap-1.5 border border-gray-300 text-[#1d1d1f] hover:border-[#1d1d1f] px-6 py-3 rounded-full text-[14px] font-semibold transition-all"
          data-testid="non-live-state-one-on-one-cta"
        >
          Talk it through with a human
        </Link>
      </div>
      <p className="text-[12px] text-gray-500 mt-5 leading-[1.55] max-w-[580px] mx-auto">
        The BuildPilot workspace, Building Glossary, Cost Tracker and every "how the build works" guide are national and work today.
      </p>
    </div>
  </section>
);

const VicExploreStrip = () => (
  <section className="py-9 sm:py-20 bg-white border-t border-gray-100" data-testid="vic-explore-strip">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5 sm:mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-2 h-2 rounded-full bg-[#00a550] animate-pulse" />
            <p className="text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.04em] text-[#0d3d1e]">
              Live in Victoria
            </p>
          </div>
          <h2 className="text-[26px] sm:text-[36px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1]">
            <span className="gradient-text-blue">What we cover</span> in Victoria today
          </h2>
          <p className="text-[15px] text-[#86868b] mt-2 max-w-[640px] leading-[1.55]">
            Grants, councils and suburb research consolidated from official Victorian Government sources. Live builder matching for Melbourne is available now.
          </p>
        </div>
        <Link
          to="/vic"
          className="inline-flex items-center justify-center gap-1.5 bg-[#1d1d1f] text-white hover:bg-black px-5 py-3 rounded-full text-[13.5px] font-semibold transition-all shrink-0"
          data-testid="vic-explore-hub-cta"
        >
          Open the Victoria hub
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="vic-explore-grid">
        {VIC_EXPLORE_CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="group bg-[#fafbfc] border border-gray-200 rounded-2xl p-5 hover:border-[#1d1d1f] hover:bg-white transition-all flex flex-col"
            data-testid={`vic-explore-card-${c.eyebrow.toLowerCase()}`}
          >
            <h3 className="text-[17px] font-bold text-[#1d1d1f] leading-[1.3] mb-2 group-hover:text-[#1d8efe] transition-colors">
              {c.title}
            </h3>
            <p className="text-[13.5px] text-gray-600 leading-[1.55] mb-4 flex-1">
              {c.body}
            </p>
            <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors">
              Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// National evergreen guides - surfaced on the homepage so they accrue
// internal link equity and funnel visitors down to the local pages.
const NATIONAL_GUIDE_LINKS = [
  { to: '/cost-to-build-a-house-australia', title: 'Cost to build a house', blurb: 'National 2026 per-m² bands and example homes.' },
  { to: '/how-to-build-a-home', title: 'How to build a home', blurb: 'The 8 stages, start to handover.' },
  { to: '/renovation-costs-australia', title: 'Renovation costs', blurb: 'Kitchen, bathroom, whole-home and extension ranges.' },
  { to: '/knockdown-rebuild-explained', title: 'Knockdown rebuild', blurb: 'Process, timeline and when it beats renovating.' },
  { to: '/first-home-buyer-guide-australia', title: 'First home buyer guide', blurb: 'Grants, schemes and the first steps.' },
  { to: '/granny-flat-costs-australia', title: 'Granny flat costs', blurb: 'Kit vs built-on-site, and what drives the price.' },
];

// Build-type → which national guide(s) to float to the front. Keyed by the
// resolved journey project slug (journeyResolver / improvementJourney). The
// full set always renders; we only reorder so a visitor's current project
// leads. No signal yet → default order.
const PROJECT_GUIDE_PRIORITY = {
  'renovations': ['/renovation-costs-australia'],
  'building-a-new-home': ['/cost-to-build-a-house-australia', '/how-to-build-a-home'],
  'knockdown-rebuild': ['/knockdown-rebuild-explained'],
  'first-home-buyer-adelaide': ['/first-home-buyer-guide-australia'],
  'house-and-land-adelaide': ['/cost-to-build-a-house-australia', '/first-home-buyer-guide-australia'],
  'choosing-land': ['/cost-to-build-a-house-australia', '/how-to-build-a-home'],
  'home-design': ['/how-to-build-a-home', '/cost-to-build-a-house-australia'],
  'property-development': ['/cost-to-build-a-house-australia'],
  'sheds-garages': ['/granny-flat-costs-australia'],
  'decking': ['/renovation-costs-australia'],
  'landscaping': ['/renovation-costs-australia'],
  'swimming-pools': ['/renovation-costs-australia'],
  'verandahs-pergolas': ['/renovation-costs-australia'],
  'interior-design': ['/renovation-costs-australia'],
  'changing-builders': ['/how-to-build-a-home'],
};

function orderGuidesByProject(projectSlug) {
  const priority = PROJECT_GUIDE_PRIORITY[projectSlug] || [];
  if (!priority.length) return NATIONAL_GUIDE_LINKS;
  const lead = [];
  priority.forEach((p) => {
    const g = NATIONAL_GUIDE_LINKS.find((x) => x.to === p);
    if (g && !lead.includes(g)) lead.push(g);
  });
  const rest = NATIONAL_GUIDE_LINKS.filter((g) => !lead.includes(g));
  return [...lead, ...rest];
}

const NationalGuidesStrip = () => {
  const userState = useUserState();
  const stateName = STATE_NAMES[userState] || 'your state';
  // SA (and every non-VIC default) lands on the national guides index;
  // VIC gets its dedicated hub.
  const hubTo = userState === 'VIC' ? '/vic' : '/guides';
  // Reorder by the visitor's resolved build type (anonymous-safe, reads
  // localStorage). Computed after mount so SSR/first paint stays stable.
  const [guides, setGuides] = useState(NATIONAL_GUIDE_LINKS);
  useEffect(() => {
    try {
      const { project } = resolveJourney();
      if (project && project.slug) setGuides(orderGuidesByProject(project.slug));
    } catch { /* keep default order */ }
  }, []);

  return (
    <section className="py-11 sm:py-16 bg-[#fafbfc] border-t border-gray-100" data-testid="national-guides-strip">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center sm:flex-row sm:items-end sm:justify-between sm:text-left gap-3 mb-6 sm:mb-8">
          <div>
            <h2 className="text-[22px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-[-0.02em] leading-[1.15]">
              National guides for every path
            </h2>
            <p className="text-[14px] sm:text-[15px] text-[#86868b] mt-2 max-w-[560px] leading-[1.55]">
              Plain-English, Australia-wide explainers on costs and process - then live detail for {stateName}.
            </p>
          </div>
          <Link to={hubTo} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1d1d1f] hover:text-[#1d8efe] transition-colors shrink-0" data-testid="national-guides-see-all">
            All guides <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {/* Mobile: horizontal swipe carousel (edge-bleed, snap). sm+: static grid. */}
        <div
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-1
                     sm:mx-0 sm:px-0 sm:pb-0 sm:overflow-visible sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4
                     [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="national-guides-grid"
        >
          {guides.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#1d1d1f] transition-all flex flex-col
                         shrink-0 w-[78%] snap-start sm:w-auto sm:shrink"
              data-testid={`national-guide-card-${g.to.replace(/^\//, '')}`}
            >
              <h3 className="text-[16px] font-bold text-[#1d1d1f] leading-[1.3] mb-1.5 group-hover:text-[#1d8efe] transition-colors">{g.title}</h3>
              <p className="text-[13px] text-[#86868b] leading-[1.5] mb-4 flex-1">{g.blurb}</p>
              <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors">
                Read guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Home Styles & Eras spotlight - look-first discovery entry into the hub.
const HomeStylesSpotlight = () => (
  <section className="py-11 sm:py-16 bg-white border-t border-gray-100" data-testid="home-styles-spotlight">
    <div className="max-w-7xl mx-auto px-5 sm:px-6">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-[22px] sm:text-[28px] font-semibold text-[#1d1d1f] leading-tight">Find your home style</h2>
          <p className="text-[#86868b] text-sm sm:text-[15px] mt-1.5 max-w-xl">
            From heritage Victorian and Edwardian to Hamptons, coastal and the modern farmhouse. See what defines each
            look, the builder designs that match it, and how to furnish it.
          </p>
        </div>
        <Link
          to="/home-styles"
          className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1d1d1f] hover:text-[#1d8efe] transition-colors shrink-0"
          data-testid="home-styles-spotlight-see-all"
        >
          All styles &amp; eras <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="home-styles-spotlight-grid">
        {HOME_STYLE_GROUPS.flatMap((g) => g.slugs).map((slug) => {
          const s = HOME_STYLES[slug];
          if (!s) return null;
          return (
            <Link
              key={slug}
              to={`/home-styles/${slug}`}
              className="group rounded-2xl overflow-hidden border border-[#e8e8ed] bg-white hover:border-[#1d1d1f] hover:shadow-md transition-all"
              data-testid={`home-styles-spotlight-card-${slug}`}
            >
              <div className="aspect-[4/3] bg-[#eef0f2] overflow-hidden">
                {s.heroImage ? (
                  <img
                    src={s.heroImage}
                    alt={s.heroImageAlt || `${s.name} home style`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-[11px] uppercase tracking-[0.08em] text-[#b0b4ba]">{s.name}</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="text-[10.5px] uppercase tracking-[0.05em] text-[#86868b] mb-1">{s.era}</div>
                <div className="text-[15px] font-semibold text-[#1d1d1f] group-hover:text-[#1d8efe] transition-colors leading-snug">{s.name}</div>
              </div>
            </Link>
          );
        })}
      </div>
      <Link
        to="/home-styles"
        className="sm:hidden inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1d8efe] mt-5"
        data-testid="home-styles-spotlight-see-all-mobile"
      >
        All styles &amp; eras <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);


const HomePage = () => {
  const userState = useUserState();
  const isSa = userState === 'SA';
  const isVic = userState === 'VIC';
  const internalLinks = isVic ? VIC_INTERNAL_LINKS : SA_INTERNAL_LINKS;

  // Homepage H&L packages are now shown ONLY when we can tailor them to the
  // visitor: an identified user (name + email captured this session) who is
  // actually in a new-build / land journey. Anonymous visitors (incl. crawlers)
  // don't see the card grid on the homepage - they still reach H&L via the menu
  // and the crawlable internal-links footer. Mirrors realestate.com.au, whose
  // listing grids are gated to logged-in intent and whose homepage H&L is just
  // footer text links.
  const { hasContact } = useSessionContact();
  const showTailoredPackages = (() => {
    if (!hasContact || !isLiveState(userState)) return false;
    try {
      const { project } = resolveJourney();
      return Boolean(project && HL_INTENT_PROJECTS.has(project.slug));
    } catch {
      return false;
    }
  })();

  // State-aware homepage meta. Default is NEUTRAL/national so crawlers and
  // first-time visitors (no state picked) get the dual-city national title;
  // it flips to Adelaide or Melbourne only when a visitor explicitly selects
  // SA or VIC in the header state chip.
  const cityLabel = isVic ? 'Melbourne' : isSa ? 'Adelaide' : null;
  const metaTitle = cityLabel
    ? `Australia's Home for Building | Compare ${cityLabel} Builders & 760+ Designs 2026`
    : `Australia's Home for Building | Compare Builders, Designs & Costs 2026`;
  const metaDescription = isVic
    ? 'BuildPilot is the independent way to compare Melbourne partner builders, browse 760+ home designs and find live house & land packages across Victoria. Real prices, honest reads, optional One on One Support.'
    : isSa
    ? 'BuildPilot is the independent way to compare Adelaide partner builders, browse 760+ home designs, explore 68 suburb guides and find live house & land packages. Real prices, honest reads, optional One on One Support.'
    : 'BuildPilot is the independent way to compare partner builders, browse 760+ home designs and find live house & land packages across Adelaide and Melbourne. Real prices, honest reads, optional One on One Support.';
  const metaKeywords = isVic
    ? 'home builders Melbourne, compare Melbourne builders, new home builders Melbourne, Melbourne home designs, house and land packages Melbourne, Victoria home builders, Melbourne build CoPilot'
    : isSa
    ? 'home builders Adelaide, compare Adelaide builders, new home builders Adelaide, Adelaide home designs, Adelaide suburb guides, knockdown rebuild Adelaide, house and land packages Adelaide, Adelaide build CoPilot'
    : 'home builders australia, compare home builders, new home builders adelaide, home builders melbourne, home designs, house and land packages adelaide, house and land packages melbourne, build cost calculator';
  const ogDescription = isVic
    ? 'Compare Melbourne partner builders, browse 760+ designs and find live house & land across Victoria. Plus One on One Support when you want a real human in your corner.'
    : isSa
    ? 'Compare Adelaide partner builders, browse 760+ designs and 68 suburb guides. Plus One on One Support when you want a real human in your corner.'
    : 'Compare partner builders, browse 760+ designs and find live house & land across Adelaide and Melbourne. Plus One on One Support when you want a real human in your corner.';
  const twitterTitle = isVic
    ? "BuildPilot Melbourne | Melbourne's Home-Build CoPilot"
    : isSa
    ? "BuildPilot Adelaide | Adelaide's Home-Build CoPilot"
    : "BuildPilot | Australia's Home-Build CoPilot";

  // Belt-and-braces: keep the browser tab title in sync with the selected
  // state. The inline SEO script in index.html sets an initial title for "/",
  // so we assign directly here to guarantee it flips SA<->VIC.
  useEffect(() => {
    document.title = metaTitle;
  }, [metaTitle]);
  const internalLinksTitle = isVic ? 'Explore Victoria Home Project Guides' : 'Explore Our Home Project Guides';
  const internalLinksSubtitle = isVic
    ? 'Everything we have consolidated for Melbourne buyers today'
    : 'Expert resources for planning, building, renovating and improving';

 // Hero background video (`/videos/hero-background.mp4`) is DECORATIVE
 // ambient motion, not primary page content. We deliberately do NOT
 // emit a VideoObject schema for it - Google's Video indexing report
 // flags such pages as "Video isn't on a watch page" because a home
 // page's primary purpose is not watching that specific clip. The
 // <video> element itself is marked aria-hidden below.

 const breadcrumbSchema = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 "itemListElement": [{
 "@type": "ListItem",
 "position": 1,
 "name": "Home",
 "item": "https://buildpilot.com.au"
 }]
 };

 // FAQ Schema for "People Also Ask" featured snippets - targeting Adelaide keywords
 const faqSchema = {
 "@context": "https://schema.org",
 "@type": "FAQPage",
 // Speakable spec lets voice assistants (Siri / Google Assistant /
 // Alexa) read FAQ Q&A aloud. The CSS selector matches the FAQ
 // accordion triggers and bodies on this page.
 "speakable": {
   "@type": "SpeakableSpecification",
   "cssSelector": ["h2", "[data-radix-collection-item]"]
 },
 "mainEntity": [
 {
 "@type": "Question",
 "name": "Is BuildPilot only for new homes?",
 "acceptedAnswer": {
 "@type": "Answer",
 "text": "No. BuildPilot covers the whole home project lifecycle - planning, building, renovating, extending and improving. Whether you're comparing new-home builders, planning a renovation, adding a pool, deck or veranda, developing a block, or finishing off and moving in, we line up the right people, products and tools for the stage you're actually at. New-home building is where our content is deepest, but it's only one of the projects we help with."
 }
 },
 {
 "@type": "Question",
 "name": "How does BuildPilot rank builders?",
 "acceptedAnswer": {
 "@type": "Answer",
 "text": "Builders aren't ranked best-to-worst. We rank them by how closely each builder's stated preferences and capabilities fit what you've told us about your project - your build type, block, budget band, suburb and timeline. The Gold mark next to a builder means they're the closest match for your brief specifically. Change your brief and the matches change with it. We believe every builder is great at what they do; they just need the right projects that suit their business."
 }
 },
 {
 "@type": "Question",
 "name": "Which states does BuildPilot cover?",
 "acceptedAnswer": {
 "@type": "Answer",
 "text": "Our content is deepest in South Australia (Adelaide) and Victoria (Melbourne), both live and growing fast. The matcher, comparison tools and cost calculators work nationally, and any builder whose stated coverage includes NSW, QLD or beyond can be matched to a customer in those states. Where a tool or guide is state-specific, we say so on the page so you know what applies to you."
 }
 },
 {
 "@type": "Question",
 "name": "How do you verify builders?",
 "acceptedAnswer": {
 "@type": "Answer",
 "text": "We do not certify any builder's licence or insurance status on your behalf - both change too often to stand behind responsibly. Instead, BuildPilot ships two free tools: Builder Licence Check (deep links to every Australian state register) and Building Insurance Check (explains the home-warranty scheme that applies in your state and what to ask for). On every builder profile you will see a Check their licence and Check their insurance button that opens those tools with the builder's name and state pre-filled."
 }
 },
 {
 "@type": "Question",
 "name": "Can I save my build brief and pick up later?",
 "acceptedAnswer": {
 "@type": "Answer",
 "text": "Yes. The moment you start a matcher we save your answers (build type, budget, suburb, bedrooms, priorities) to your anonymous brief - no signup needed. The brief auto-fills every form on the site, ranks home designs and suburbs by % match to what you said you want, and syncs to your CoPilot the moment you sign in. Tell us once, never re-enter it."
 }
 },
 {
 "@type": "Question",
 "name": "Can I choose my own builder?",
 "acceptedAnswer": {
 "@type": "Answer",
 "text": "Absolutely. The matcher and rankings are guidance, not a gate. If you've got a builder in mind, search for them in our directory, save them to your shortlist, request quotes, and run their offer through Quote Comparison alongside any of our matched picks. Your decision, always."
 }
 }
 ]
 };

 // Service Schema for rich results
 const serviceSchema = {
 "@context": "https://schema.org",
 "@type": "Service",
 "name": "Home-Build CoPilot",
 "provider": {
 "@id": "https://buildpilot.com.au/#organization"
 },
 "areaServed": {
 "@type": "Country",
 "name": "Australia"
 },
 "description": "Your CoPilot to building in Australia. We connect families with partner builders, 760+ designs and local suburb guides across Adelaide, Melbourne and beyond.",
 "serviceType": "Home Build CoPilot"
 };

 // ItemList - points search engines + AI engines at the canonical
 // builders index for the visitor's live state. SA emits the Adelaide
 // dataset, VIC emits our Melbourne partner directory, and a neutral /
 // out-of-area visitor emits no city-specific builder list (generic).
 const SA_BUILDERS_INDEX_SCHEMA = {
 "@context": "https://schema.org",
 "@type": "ItemList",
 "name": "Adelaide Home Builders ranked by BuildPilot",
 "description": "BuildPilot's continuously updated ranking of listed Adelaide home builders. Methodology and full dataset at /adelaide-builders-index.",
 "url": "https://buildpilot.com.au/adelaide-builders-index",
 "itemListOrder": "https://schema.org/ItemListOrderDescending",
 "itemListElement": [
   { "@type": "ListItem", "position": 1, "url": "https://buildpilot.com.au/builders/metricon", "name": "Metricon" },
   { "@type": "ListItem", "position": 2, "url": "https://buildpilot.com.au/builders/fairmont-homes", "name": "Fairmont Homes" },
   { "@type": "ListItem", "position": 3, "url": "https://buildpilot.com.au/builders/simonds", "name": "Simonds Homes" },
   { "@type": "ListItem", "position": 4, "url": "https://buildpilot.com.au/builders/rossdale-homes", "name": "Rossdale Homes" },
   { "@type": "ListItem", "position": 5, "url": "https://buildpilot.com.au/builders/abc-homes", "name": "ABC Homes" },
   { "@type": "ListItem", "position": 6, "url": "https://buildpilot.com.au/builders/sterling-homes", "name": "Sterling Homes" },
   { "@type": "ListItem", "position": 7, "url": "https://buildpilot.com.au/builders/hickinbotham", "name": "Hickinbotham" },
   { "@type": "ListItem", "position": 8, "url": "https://buildpilot.com.au/builders/format-homes", "name": "Format Homes" },
   { "@type": "ListItem", "position": 9, "url": "https://buildpilot.com.au/builders/oakford-homes", "name": "Oakford Homes" },
   { "@type": "ListItem", "position": 10, "url": "https://buildpilot.com.au/builders/statesman-homes", "name": "Statesman Homes" },
   { "@type": "ListItem", "position": 11, "url": "https://buildpilot.com.au/builders/construction-services-australia", "name": "Construction Services Australia" }
 ]
 };

 // Melbourne partner directory - ONLY the Victorian builders BuildPilot
 // actually partners with (presence, never ranked). Matches /melbourne-builders.
 const VIC_BUILDERS_INDEX_SCHEMA = {
 "@context": "https://schema.org",
 "@type": "ItemList",
 "name": "Melbourne Home Builders partnered with BuildPilot",
 "description": "The Victorian builders BuildPilot partners with and vets for new homes, knockdown rebuilds and renovations across Melbourne. Full directory at /melbourne-builders.",
 "url": "https://buildpilot.com.au/melbourne-builders",
 "itemListElement": [
   { "@type": "ListItem", "position": 1, "url": "https://buildpilot.com.au/builders/metricon", "name": "Metricon" },
   { "@type": "ListItem", "position": 2, "url": "https://buildpilot.com.au/builders/simonds", "name": "Simonds Homes" },
   { "@type": "ListItem", "position": 3, "url": "https://buildpilot.com.au/builders/abc-homes", "name": "Australian Building Company" },
   { "@type": "ListItem", "position": 4, "url": "https://buildpilot.com.au/builders/burra-built", "name": "Burra Built" },
   { "@type": "ListItem", "position": 5, "url": "https://buildpilot.com.au/professionals/halex-group", "name": "Halex Group" }
 ]
 };

 const buildersIndexSchema = isVic ? VIC_BUILDERS_INDEX_SCHEMA : isSa ? SA_BUILDERS_INDEX_SCHEMA : null;

 return (
 <div className="min-h-screen">
 <Helmet>
 {/* Primary Meta Tags */}
 <title>{metaTitle}</title>
 <meta name="title" content={metaTitle} />
 <meta name="description" content={metaDescription} />
 <meta name="keywords" content={metaKeywords} />
 <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
 <link rel="canonical" href="https://buildpilot.com.au" />
 
 {/* Open Graph / Facebook */}
 <meta property="og:type" content="website" />
 <meta property="og:url" content="https://buildpilot.com.au" />
 <meta property="og:title" content={metaTitle} />
 <meta property="og:description" content={ogDescription} />
 <meta property="og:image" content="https://buildpilot.com.au/images/og-image-v2.png" />
 <meta property="og:image:width" content="1200" />
 <meta property="og:image:height" content="630" />
 <meta property="og:site_name" content="BuildPilot" />
 <meta property="og:locale" content="en_AU" />
 
 {/* Twitter */}
 <meta name="twitter:card" content="summary_large_image" />
 <meta name="twitter:url" content="https://buildpilot.com.au" />
 <meta name="twitter:title" content={twitterTitle} />
 <meta name="twitter:description" content={ogDescription} />
 <meta name="twitter:image" content="https://buildpilot.com.au/images/og-image-v2.png" />
 
 {/* Schema.org - Organization/WebSite/LocalBusiness live site-wide in public/index.html (#organization graph). Homepage emits only its unique nodes to avoid duplicate/conflicting entities. */}
 <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
 <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
 <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
 {buildersIndexSchema && <script type="application/ld+json">{JSON.stringify(buildersIndexSchema)}</script>}
 </Helmet>

 <Header variant="light" />
 <main>
 <HeroSection />
 <FourPillarsStrip />
 <ProcessSection />
 <JourneyResume />
 <TrustStrip />
 <SeasonStrip />
 <Reveal><AppPromoSection /></Reveal>
 <Reveal><ShortlistStartersSection /></Reveal>
 <Reveal><CollectionsStrip /></Reveal>
 <Reveal><HomeStylesSpotlight /></Reveal>
 <Reveal><NationalGuidesStrip /></Reveal>
 {/* Live H&L packages strip - now shown on the homepage ONLY for an
     identified visitor with genuine new-build/land intent (see
     showTailoredPackages). Anonymous visitors + crawlers no longer see the
     grid here; it stays reachable via the menu and the H&L hub. Non-live
     states still get the coverage callout. */}
 {userState && !isLiveState(userState)
   ? <Reveal><NonLiveStateCallout stateName={STATE_NAMES[userState] || userState} stateCode={userState} /></Reveal>
   : showTailoredPackages
     ? <Reveal><HomepageLivePackagesStrip /></Reveal>
     : null}
 <Reveal><HomeFinishedBuildsReel /></Reveal>
 <LiveDataStrip />
 <Reveal>
   <LatestNewsStrip
     title={'The latest from BuildPilot'}
     subtitle={isVic
       ? 'Independent reads on building in Victoria, grants, councils and process. Updated regularly.'
       : isSa
       ? 'Independent reads on the Adelaide home & build market, costs and process. Updated regularly.'
       : 'Independent reads on the Australian home & build market, costs and process. Updated regularly.'}
   />
 </Reveal>
 <Reveal><FeaturedOnStrip /></Reveal>
 <Reveal><RegionGrid /></Reveal>

 <Reveal><BuilderPartnersSection /></Reveal>
 <Reveal><FAQSection /></Reveal>
 
 {/* Internal Links Section - swaps per state */}
 <section className="py-10 sm:py-14 bg-[#f5f5f7]" data-testid="internal-links-section" data-links-state={userState}>
 <div className="max-w-7xl mx-auto px-5 sm:px-6">
 <h2 className="text-[20px] sm:text-2xl font-semibold text-[#1d1d1f] mb-1.5 text-center sm:text-left">{internalLinksTitle}</h2>
 <p className="text-[#86868b] text-sm mb-6 text-center sm:text-left">{internalLinksSubtitle}</p>
 <div className="columns-2 sm:columns-3 lg:columns-4 gap-x-8">
 {internalLinks.map((link, i) => (
 <a
 key={i}
 href={link.to}
 className="block break-inside-avoid py-[7px] text-[13.5px] text-[#424245] hover:text-[#1d8efe] transition-colors"
 >
 {link.label}
 </a>
 ))}
 </div>
 </div>
 </section>

 <CTASection />
 </main>
 <Footer />

 
    {/* Additional SEO Schemas */}
    <SiteNavigationSchema />
    <SEOSchemas 
      includeLocalBusiness={false}
      includeReviews={false}
      includeWebsite={false}
    />
    {/* AI Overview / AEO citation schema. Targets brand-level queries
        ("What is BuildPilot?", "Who is BuildPilot for?") that LLMs
        ask when a user names the site. Answer + Dataset + speakable
        + mentions are wired via <AIOverviewSchema>. No visible block
        on the homepage - the hero already covers that role. */}
    <AIOverviewSchema
      question="What is BuildPilot?"
      answer="BuildPilot is Australia's independent build companion for home buyers, homeowners and industry professionals, with live builder matching in Adelaide and Melbourne and expanding across Australia. Founded by George Giannakakis (Bachelor of Architectural Studies, Master of Architecture UniSA, RLA300580), BuildPilot helps Australians compare partner builders, browse 760+ indexed home designs, walk through local suburb guides and read independent build cost reports. Optional One on One Support pairs users with a real human for builder shortlists, contract review and mid-build oversight. BuildPilot is not affiliated with any single builder."
      url="/"
      mentions={['BuildPilot', 'Adelaide home builders', 'New Home Construction', 'House and Land Packages', 'Knockdown Rebuild', 'First Home Buyer', 'Building Contracts', 'One on One Support']}
    />
 </div>
 );
};

export default HomePage;